import { useEffect, useRef, useState } from "react";

const SLIDES = [
  { kicker: "Education", title: "Know before you invest", subtitle: "Not after you regret" },
  { kicker: "Approach", title: "Personalised advice", subtitle: "Based on your goals, not inventory" },
  { kicker: "Outcome", title: "Curated opportunities", subtitle: "Only what actually makes sense" },
];

const DURATION = 5200;

export default function HeroSlider() {
  const [active, setActive] = useState(0);
  const [progress, setProgress] = useState(0);
  const startRef = useRef(Date.now());
  const rafRef = useRef(null);

  useEffect(() => {
    startRef.current = Date.now();
    setProgress(0);
    const tick = () => {
      const elapsed = Date.now() - startRef.current;
      const p = Math.min(1, elapsed / DURATION);
      setProgress(p);
      if (p >= 1) {
        setActive((a) => (a + 1) % SLIDES.length);
      } else {
        rafRef.current = requestAnimationFrame(tick);
      }
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [active]);

  const goTo = (i) => {
    if (i === active) return;
    setActive(i);
  };

  return (
    <div
      className="relative w-full rounded-3xl overflow-hidden bg-white border border-zinc-200 shadow-[0_24px_60px_-24px_rgba(29,78,216,0.22)]"
      data-testid="hero-slider"
      aria-roledescription="carousel"
    >
      {/* Decorative animated background */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="absolute -right-12 -top-12 w-56 h-56 rounded-full bg-[#1D4ED8]/12 blur-3xl animate-pulse" />
        <div className="absolute -left-10 bottom-0 w-44 h-44 rounded-full bg-[#1D4ED8]/8 blur-2xl" />
        <svg className="absolute inset-0 w-full h-full opacity-[0.06]" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id="hsg" width="6" height="6" patternUnits="userSpaceOnUse">
              <path d="M 6 0 L 0 0 0 6" fill="none" stroke="#1D4ED8" strokeWidth="0.4" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#hsg)" />
        </svg>
      </div>

      <div className="relative px-6 sm:px-7 pt-6 sm:pt-7 pb-5">
        {/* Top row: large number + counter */}
        <div className="flex items-start justify-between">
          <div className="relative w-[100px] h-[112px] sm:w-[112px] sm:h-[124px] overflow-hidden">
            {SLIDES.map((_, i) => (
              <div
                key={i}
                className="absolute inset-0 flex items-center justify-center font-display tabular-nums leading-[1] text-[88px] sm:text-[96px] text-[#1D4ED8] tracking-tight transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
                style={{
                  opacity: active === i ? 1 : 0,
                  transform:
                    active === i
                      ? "translateY(0)"
                      : i < active
                      ? "translateY(-110%)"
                      : "translateY(110%)",
                }}
                aria-hidden={active !== i}
              >
                0{i + 1}
              </div>
            ))}
          </div>

          <div className="flex flex-col items-end">
            <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-zinc-400">
              {String(active + 1).padStart(2, "0")} / {String(SLIDES.length).padStart(2, "0")}
            </div>
            <div className="mt-3 flex flex-col items-end gap-1.5" role="tablist" aria-label="Hero slides">
              {SLIDES.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  role="tab"
                  aria-selected={active === i}
                  aria-label={`Show slide ${i + 1}`}
                  onClick={() => goTo(i)}
                  data-testid={`hero-slide-dot-${i}`}
                  className={`rounded-full transition-all duration-300 ${
                    active === i ? "w-6 h-1.5 bg-[#1D4ED8]" : "w-1.5 h-1.5 bg-zinc-300 hover:bg-zinc-400"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Kicker */}
        <div className="mt-3 h-4 relative overflow-hidden">
          {SLIDES.map((sl, i) => (
            <span
              key={i}
              className="absolute inset-0 text-[10px] uppercase tracking-[0.2em] font-semibold text-[#1D4ED8] transition-all duration-500"
              style={{
                opacity: active === i ? 1 : 0,
                transform: active === i ? "translateY(0)" : "translateY(-100%)",
              }}
              aria-hidden={active !== i}
            >
              {sl.kicker}
            </span>
          ))}
        </div>

        {/* Title */}
        <div className="relative mt-2 h-[64px] sm:h-[68px] overflow-hidden">
          {SLIDES.map((sl, i) => (
            <h3
              key={i}
              className="absolute inset-0 font-display text-[26px] sm:text-[28px] leading-[1.1] text-zinc-900 tracking-tight transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
              style={{
                opacity: active === i ? 1 : 0,
                transform:
                  active === i ? "translateY(0)" : i < active ? "translateY(-110%)" : "translateY(110%)",
                transitionDelay: active === i ? "80ms" : "0ms",
              }}
              aria-hidden={active !== i}
            >
              {sl.title}
            </h3>
          ))}
        </div>

        {/* Subtitle */}
        <div className="relative mt-1 h-6 overflow-hidden">
          {SLIDES.map((sl, i) => (
            <p
              key={i}
              className="absolute inset-0 text-sm text-zinc-500 transition-all duration-700 ease-[cubic-bezier(0.22,1,0.36,1)]"
              style={{
                opacity: active === i ? 1 : 0,
                transform:
                  active === i ? "translateY(0)" : i < active ? "translateY(-110%)" : "translateY(110%)",
                transitionDelay: active === i ? "180ms" : "0ms",
              }}
              aria-hidden={active !== i}
            >
              {sl.subtitle}
            </p>
          ))}
        </div>
      </div>

      {/* Bottom progress strip */}
      <div className="relative h-[3px] bg-zinc-100 overflow-hidden">
        <div
          className="absolute inset-y-0 left-0 bg-[#1D4ED8] rounded-r-full"
          style={{ width: `${progress * 100}%`, transition: progress === 0 ? "none" : "width 80ms linear" }}
        />
      </div>
    </div>
  );
}
